import { Group, Box, Stack, Affix, CloseIcon, Overlay, OverlayProps, StyleProp } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ActionIcon } from 'components/action-icon/ActionIcon';
import { Badge } from 'components/badge/Badge';
import { Text } from 'components/text/Text';
import { IconFilter } from '@tabler/icons-react';
import styles from './FilterDrawer.module.css';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { BadgeProps } from 'components/badge/Badge';

interface FilterDrawerProps extends PropsWithChildren {
    title: ReactNode;
    activeFilterCount?: number;
    height?: StyleProp<React.CSSProperties['height']>;
    /**
     * Mantine overlay props for overlay components
     */
    overlayProps?: OverlayProps;
    /**
     * Renders a transparent overlay behind the filter drawer when opened. Defaults to true
     */
    withOverlay?: boolean;
    /**
     * Props for the badge component used to display the active filter count.
     */
    badgeProps?: BadgeProps;
}

export const FilterDrawer: FC<FilterDrawerProps> = ({
    title,
    activeFilterCount = 1,
    height = '100vh',
    children,
    overlayProps,

    withOverlay = true,
    badgeProps,
}) => {
    const [opened, { toggle, close }] = useDisclosure();

    return (
        <>
            <Affix position={{ bottom: 0, right: 0 }} h={height}>
                <Box h={'100%'} {...(!opened && { onClick: toggle, style: { cursor: 'pointer' } })}>
                    <Stack className={styles.base} {...(opened && { w: '25rem' })}>
                        {!opened ? (
                            <Stack pt={'8px'} align={'center'}>
                                <IconFilter color={'var(--mantine-color-gray-8)'} size={16} />
                                <Badge
                                    type="number"
                                    status="prominent"
                                    aria-label={`Active filters: ${activeFilterCount}`}
                                    {...badgeProps}
                                >{`${activeFilterCount}`}</Badge>
                            </Stack>
                        ) : (
                            <>
                                <Group w={'100%'} justify={'space-between'} align={'center'}>
                                    <Text size={'1.5rem'} c={'gray.8'}>
                                        {title}
                                    </Text>
                                    <ActionIcon
                                        intent={'neutral'}
                                        appearance={'ghost'}
                                        size={'lg'}
                                        onClick={toggle}
                                        aria-label="Close filter drawer"
                                    >
                                        <CloseIcon />
                                    </ActionIcon>
                                </Group>
                                {children}
                            </>
                        )}
                    </Stack>
                </Box>
            </Affix>

            {opened && withOverlay && (
                <Overlay backgroundOpacity={0} onClick={close} zIndex={200} {...overlayProps} />
            )}
        </>
    );
};
